import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import i18n, { loadLocale } from '../i18n';
import { resolveLocale } from '../utils/languageUtils';

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (languageCode: string) => Promise<void>;
  getLocalizedText: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

// Pairs with the inline script in index.html that hides <html> for non-en
// returning visitors while the post-hydration locale switch is in flight.
// Called once the language change has been applied; defers one rAF so the
// browser paints the swapped strings before we drop the visibility guard.
const revealDocument = () => {
  if (typeof document === 'undefined') return;
  if (document.documentElement.style.visibility !== 'hidden') return;
  const reveal = () => {
    document.documentElement.style.visibility = '';
  };
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(reveal);
  } else {
    reveal();
  }
};

/**
 * Locale precedence — delegates to the shared `resolveLocale` helper so the
 * Provider, the i18next runtime, and the unit tests all share one ladder
 * (explicit → profile → device → en). Mirrors iOS AppLocaleResolver.
 */
const resolveUserLanguage = (): string =>
  resolveLocale({
    explicit: typeof localStorage !== 'undefined' ? localStorage.getItem('selectedLanguage') : null,
    profile: typeof localStorage !== 'undefined' ? localStorage.getItem('profileLanguage') : null,
    device: typeof navigator !== 'undefined' ? navigator.language : null,
  });

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initial state matches the SSR-prerendered output (always en). The mount
  // effect below resolves the user's actual locale and triggers a switch.
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const resolved = resolveUserLanguage();
    if (resolved && resolved !== currentLanguage) {
      // Locale JSON is dynamic-imported; await it before swapping so
      // i18next doesn't fall back to en while the chunk arrives.
      loadLocale(resolved).then(() => setCurrentLanguage(resolved));
    } else {
      // No locale switch needed — reveal immediately in case the inline
      // flicker guard in index.html hid the document.
      revealDocument();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
    // GUI-75: keep <html lang> aligned with i18next so screen readers, search
    // engines, and Playwright precedence specs all see the same value.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage;
    }
    // After the first locale switch settles, undo the inline flicker guard.
    revealDocument();
  }, [currentLanguage]);

  const setLanguage = async (languageCode: string) => {
    await loadLocale(languageCode);
    setCurrentLanguage(languageCode);
    localStorage.setItem('selectedLanguage', languageCode);
  };

  const getLocalizedText = (key: string): string => {
    return i18n.t(key);
  };

  const value = {
    currentLanguage,
    setLanguage,
    getLocalizedText,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
